import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import {
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Router,
    RouterOutlet
} from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { environment } from '@environments/environment';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { filter, Subject, takeUntil } from 'rxjs';

import { BreadcrumbComponent } from './common/breadcrumb/breadcrumb.component';
import { FooterComponent } from './common/footer/footer.component';
import { LoadingComponent } from './common/loading/loading.component';
// import { ScrollTopComponent } from './common/scroll-top/scroll-top.component';
import { MinimalToolbarComponent } from './common/minimal-toolbar/minimal-toolbar.component';
import { SidenavComponent } from './common/sidenav/sidenav.component';
import { ToolbarComponent } from './common/toolbar/toolbar.component';
import { AuthService } from './services/auth.service';
import { LayoutService } from './services/layout.service';
import { MatomoService } from './services/matomo.service';

@Component( {
    selector: 'app-root',
    imports: [
        CommonModule,
        RouterOutlet,
        LoadingComponent,
        ToolbarComponent,
        SidenavComponent,
        FooterComponent,
        BreadcrumbComponent,
        MinimalToolbarComponent
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
} )
export class AppComponent implements OnInit, OnDestroy {
    #router = inject( Router );
    #matomoService = inject( MatomoService );
    #msalService = inject( MsalService );
    #broadcastService = inject( MsalBroadcastService );
    #authService = inject( AuthService );
    #layoutService = inject( LayoutService );

    isIframe = false;
    isLoggedIn = this.#authService.isLoggedIn;

    showCookieConsent = environment.optionalModules.showCookieConsent;

    protected readonly isDefaultLayout = this.#layoutService.isLayout( 'default' );
    protected readonly isMinimalLayout = this.#layoutService.isLayout( 'minimal' );

    isHome = signal( false );
    skip2main = signal( '' );
    skip2footer = signal( '' );
    isTrackingPage = false;

    readonly #destroyed: Subject<void> = new Subject<void>();

    constructor() {
        if ( environment.production ) {
            const angularPlugin: any = new AngularPlugin();
            const appInsights = new ApplicationInsights( {
                config: {
                    connectionString: environment.azure.applicationInsights,
                    extensions: [ angularPlugin ],
                    extensionConfig: {
                        [ angularPlugin.identifier ]: { router: this.#router }
                    }
                }
            } );
            appInsights.loadAppInsights();
        }
    }

    ngOnInit() {
        this.#msalService.handleRedirectObservable().subscribe();

        this.isIframe = window !== window.parent && !window.opener;

        this.#msalService.instance.enableAccountStorageEvents();

        this.#broadcastService.msalSubject$
            .pipe(
                filter(
                    ( msg: EventMessage ) =>
                        msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
                )
            )
            .subscribe( () => {
                if ( this.#msalService.instance.getAllAccounts().length === 0 ) {
                    window.location.pathname = '/';
                } else {
                    this.setLoginState();
                }
            } );

        this.#broadcastService.inProgress$
            .pipe(
                filter( ( status: InteractionStatus ) => status === InteractionStatus.None ),
                takeUntil( this.#destroyed )
            )
            .subscribe( () => {
                this.setLoginState();
                this.checkAndSetActiveAccount();
            } );

        this.#router.events
            .pipe(
                filter(
                    ( event ) =>
                        event instanceof NavigationStart ||
                        event instanceof NavigationEnd ||
                        event instanceof NavigationCancel ||
                        event instanceof NavigationError
                )
            )
            .subscribe( ( event ) => {
                if ( event instanceof NavigationEnd ) {
                    let currentUrl = this.#router.url;
                    this.isHome.set( currentUrl === '/' );

                    const hashIndex = currentUrl.indexOf( '#' );
                    if ( hashIndex > 0 ) currentUrl = currentUrl.slice( 0, hashIndex );

                    this.skip2main.set( currentUrl + '#main' );
                    this.skip2footer.set( currentUrl + '#footer' );

                    this.#matomoService.trackPageView( event.urlAfterRedirects );

                    const checked = this.#matomoService.consentChecked();
                    if ( this.showCookieConsent && !checked ) {
                        this.manageCookies();
                    }

                    this.isTrackingPage = event.urlAfterRedirects.includes( '/tracking/not-found' );
                }
            } );
    }

    setLoginState() {
        const hasAccount = this.#msalService.instance.getAllAccounts().length > 0;
        this.#authService.isLoggedIn.set( hasAccount );
    }

    checkAndSetActiveAccount() {
        let activeAccount = this.#msalService.instance.getActiveAccount();

        if ( !activeAccount && this.#msalService.instance.getAllAccounts().length > 0 ) {
            activeAccount = this.#msalService.instance.getAllAccounts()[ 0 ];
            this.#msalService.instance.setActiveAccount( activeAccount );
        }

        if ( activeAccount ) {
            this.#authService.handleAuthResponse( activeAccount );
            this.#authService.retrieveAccessToken();
        }
    }

    manageCookies() {
        this.#matomoService.handleTrackingConsent( true );
    }

    ngOnDestroy(): void {
        this.#destroyed.next( undefined );
        this.#destroyed.complete();
    }
}
